import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LinkComponent } from '../../atoms/link/link.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { TeaserComponent } from '../../molecules/teaser/teaser.component';
import { Link } from '../../../shared/interfaces/link.interface';
import LinkType from '../../../shared/enums/link-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import {
  CaasComponents,
  CaasLink,
} from '../../../shared/interfaces/caas/caas.interface';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { Teaser } from '../../../shared/interfaces/teaser.interface';
import { ManipulateDataService } from '../../../services/translate.service';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../capitalize.pipe';
import TeaserType from '../../../shared/enums/teaser.type.enums';
import { News } from '../../../shared/interfaces/news.interface';
import { SliderComponent } from '../slider/slider.component';
import { SliderType } from '../../../shared/enums/slider-type.enums';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { TeaserContainerLoaderComponent } from './teaser-container-loader/teaser-container-loader.component';

@Component({
  selector: 'hdi-teaser-container',
  standalone: true,
  imports: [
    CommonModule,
    LinkComponent,
    ButtonComponent,
    TeaserComponent,
    CapitalizePipe,
    SliderComponent,
    TeaserContainerLoaderComponent,
  ],
  templateUrl: './teaser-container.component.html',
  styleUrls: ['./teaser-container.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TeaserContainerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('teaserList') teaserList: ElementRef<HTMLElement> | undefined;
  @Input() caasData?: CaasComponents['value'][number];
  @Input() caasNewsData?: CaasComponents['value'][number];
  @Input() columns?: number;
  @Input() imageOrientation?: string;
  @Input() linkHeadline?: string;
  @Input() link?: Link;
  @Input() showMoreLess: boolean = false;
  @Input() initialVisibleTeasers: number = 4;
  @Input() newsTeasers$?: Observable<Teaser[]>;
  @Input() type?: 'standard' | 'news' | '' | 'mini';
  @Input() sectionBackgroundColor: 'white' | 'grey' = 'white';
  public readonly ButtonType = ButtonType;
  public readonly LinkType = LinkType;
  public readonly HrefType = HrefType;
  public teasers: ElementRef[] = [];
  public backupTeasers: ElementRef[] = [];
  public miniTeasers: ElementRef[] = [];
  public visible_teasers: number;
  private teaserStep: number = 4;
  private readonly destroy = new Subject<void>();
  SliderType = SliderType;
  TeaserType = TeaserType;
  currentPage: number = 1;
  totalPages: number = 1;
  isSectionGrey: boolean = false;
  isGreen: boolean = false;
  miniTeaserBadges: string[] = [];
  public isLoading = true;
  constructor(
    private readonly renderer: Renderer2,
    private readonly manipulateDataService: ManipulateDataService,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly router: Router,
    private readonly elRef: ElementRef,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.visible_teasers = this.initialVisibleTeasers;
  }

  ngAfterViewInit(): void {
    this.setupVisibility();
    this.checkParentClass();
    this.checkIfGreen();
  }

  checkIfGreen(): void {
    if (
      this.caasData?.formData?.st_background_mini?.value?.identifier === 'green'
    ) {
      this.isGreen = true;
    }
  }

  checkParentClass() {
    const parentSection = this.elRef.nativeElement.closest('section');
    if (parentSection?.classList.contains('bg-grey')) {
      this.isSectionGrey = true;
    } else {
      this.isSectionGrey = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent(changes['caasData'].currentValue);
    }
    if (changes['caasNewsData']) {
      this.setCaasNews(changes['caasNewsData'].currentValue);
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  getMiniTeaserBadge(miniTeaser: CaasComponents['value'][number] | undefined) {
    if (miniTeaser) {
      return miniTeaser.formData?.st_target_group?.value instanceof Array
        ? miniTeaser.formData?.st_target_group?.value.map(
            (option: { label: string }) => option.label
          )
        : [].filter(
            (badge: string): badge is string => typeof badge === 'string'
          ) || [];
    }
    return [];
  }

  getMiniTeaserHref(teaserMini: CaasComponents['value'][number] | undefined) {
    return this.caasBodytextService.getHref(
      teaserMini?.formData?.st_link ?? ({} as CaasLink)
    );
  }
  getMiniTeaserTarget(teaserMini: CaasComponents['value'][number] | undefined) {
    const miniTeaserHref = this.getMiniTeaserHref(teaserMini);
    return miniTeaserHref?.startsWith('http') ||
      miniTeaserHref?.startsWith('https') ||
      miniTeaserHref?.startsWith('www')
      ? '_blank'
      : '_self';
  }

  getMappedCaasData(): CaasComponents['value'][number] | undefined {
    return this.caasData;
  }

  get showMoreLessLabel(): string {
    return this.visible_teasers >= this.teasers.length
      ? 'Weniger anzeigen'
      : 'Mehr anzeigen';
  }

  get iconSelected(): string {
    return this.visible_teasers >= this.teasers.length ? 'minus' : 'plus';
  }

  setupVisibility() {
    if (this.showMoreLess) {
      this.initializeVisibleTeasers();
    }
  }

  /**
   * Initialize the visible teasers based or not on the filtered news.
   * @param filteredNews - The filtered news is specific for teaser news. It is defined only when a filter is applied.
   * @param backupTeasers - The first time news are rendered (no filter applied) a template teaser backup is saved.
   * Every time a filter is applied, initializeVisibleTeasers is triggered.
   */
  initializeVisibleTeasers(filteredNews?: News[]) {
    if (filteredNews) {
      this.teasers = Array.from(this.backupTeasers).filter((teaser) =>
        filteredNews
          .slice(3)
          .some(
            (news) => news.identifier === (teaser as unknown as HTMLElement).id
          )
      );
    } else {
      if (this.teaserList?.nativeElement?.children) {
        this.teasers = Array.from(this.teaserList.nativeElement.children)
          .map((child) => new ElementRef(child))
          .filter((teaser) => teaser); // Filter out undefined elements
      } else {
        console.warn('teaserList is undefined or has no children');
        this.teasers = [];
      }
      this.backupTeasers = this.teasers;
    }

    switch (this.columns) {
      case 3:
        this.initialVisibleTeasers = 6;
        this.visible_teasers = 6;
        this.teaserStep = 6;
        break;
      case 2:
        this.initialVisibleTeasers = 4;
        this.visible_teasers = 4;
        this.teaserStep = 4;
        break;
      case 0:
        this.visible_teasers = this.initialVisibleTeasers;
        this.teaserStep = this.initialVisibleTeasers;
        break;
      default:
        this.visible_teasers = 4;
        break;
    }

    this.teasers.forEach((teaser, index) => {
      if (teaser?.nativeElement) {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(teaser.nativeElement, 'display', 'none');
        } else {
          this.renderer.setStyle(teaser.nativeElement, 'display', 'block');
        }
      } else {
        console.warn('Invalid teaser element at index:', index);
      }
    });

    this.updateShowMoreLess(this.teasers.length, this.visible_teasers);
  }

  toggleTeasersVisibility() {
    if (this.visible_teasers < this.teasers.length) {
      this.visible_teasers += this.teaserStep;
      this.teasers?.forEach((teaser, index) => {
        if (index < this.visible_teasers && teaser?.nativeElement) {
          this.renderer.setStyle(teaser.nativeElement, 'display', 'block');
        }
      });
    } else {
      this.visible_teasers = this.initialVisibleTeasers;
      this.teasers?.forEach((teaser, index) => {
        if (index >= this.visible_teasers && teaser?.nativeElement) {
          this.renderer.setStyle(teaser.nativeElement, 'display', 'none');
        }
      });
    }
  }

  setCaasContent(caasData: CaasComponents['value'][number]) {
    safeAssign(
      () => {
        this.columns = this.setCaasColumns(caasData);
      },
      'setCaasContent.columns',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.imageOrientation =
          caasData.formData.st_image_orientation?.value?.label;
      },
      'setCaasContent.imageOrientation',
      this.errorHandler
    );

    safeAssign(
      () => {
        this.showMoreLess = this.setCaasShowMoreLess(caasData);
      },
      'setCaasContent.showMoreLess',
      this.errorHandler
    );
  }

  setCaasColumns(caasData: CaasComponents['value'][number]): number {
    switch (caasData.formData.st_layout?.value.identifier) {
      case 'horizontal': {
        const columnsHorizontal =
          caasData.formData.st_columns_horizontal?.value.identifier;
        if (columnsHorizontal === '1') {
          return 1;
        } else {
          return 2;
        }
      }
      case 'mini': {
        const columnsMiniIdentifier =
          caasData.formData.st_columns_mini?.value.identifier;
        if (columnsMiniIdentifier === '1') {
          return 2;
        } else {
          return 1;
        }
      }
      default: {
        const standardColumns =
          caasData.formData.st_columns_standard?.value.identifier;
        if (standardColumns === '2') {
          return 2;
        } else {
          return 3;
        }
      }
    }
  }

  setCaasShowMoreLess(caasData: CaasComponents['value'][number]): boolean {
    return !!caasData.formData.st_more_button?.value;
  }

  updateShowMoreLess(teasersLength: number, visibleTeasers: number) {
    if (teasersLength <= visibleTeasers) {
      this.showMoreLess = false;
    }
  }

  setCaasNews(caasNewsData: CaasComponents['value'][number]) {
    let linkUrl = '';

    // Set Link Headline
    safeAssign(
      () => {
        if (caasNewsData?.formData?.st_header?.value) {
          this.linkHeadline = caasNewsData.formData.st_header.value;
        }
      },
      'setCaasNews.linkHeadline',
      this.errorHandler
    );

    // Process Link
    safeAssign(
      () => {
        if (caasNewsData?.formData?.st_link?.value) {
          const internalLink =
            caasNewsData.formData.st_link.value.formData?.lt_link_internal
              ?.value;

          if (internalLink?.identifier) {
            linkUrl =
              this.caasRoutesService.getSeoRouteByIdentifier(
                internalLink.identifier,
                this.router.config
              )?.path ?? '/404';
          } else if (
            caasNewsData.formData.st_link.value.formData?.lt_link_external
              ?.value
          ) {
            linkUrl =
              caasNewsData.formData.st_link.value.formData.lt_link_external
                .value;
          }

          this.link = {
            label:
              caasNewsData.formData.st_link.value.formData?.lt_link_label
                ?.value ?? 'Mehr erfahren',
            href: linkUrl,
            hrefType: internalLink ? HrefType.ANGULAR_ROUTE : HrefType.EXTERNAL,
            title:
              caasNewsData.formData.st_link.value.formData?.lt_link_title
                ?.value ?? 'Mehr erfahren',
          };
        }
      },
      'setCaasNews.link',
      this.errorHandler
    );

    // Fetch News Teasers - Automatic
    safeAssign(
      () => {
        if (
          caasNewsData?.formData?.st_selection_type?.value?.identifier ===
          'automatic'
        ) {
          const filterId =
            caasNewsData?.formData?.st_filter?.value?.target?.identifier ||
            'latest';
          const quantity =
            caasNewsData?.formData?.st_quantity?.value?.identifier ?? '3';

          this.newsTeasers$ = this.manipulateDataService
            .getTeasersNewsByFilterId(filterId, quantity)
            .pipe(takeUntil(this.destroy));

          this.newsTeasers$.subscribe({
            error: (error) =>
              console.error(
                'TeaserContainer > newsTeasers$$ (automatic) subscription error:',
                error
              ),
          });
        }
      },
      'setCaasNews.newsTeasers$ (automatic)',
      this.errorHandler
    );

    // Fetch News Teasers - Manual
    safeAssign(
      () => {
        if (
          caasNewsData?.formData?.st_items?.value.length &&
          caasNewsData?.formData?.st_selection_type?.value?.identifier ===
            'manual'
        ) {
          this.newsTeasers$ = this.manipulateDataService
            .getTeasersNewsByChoice(caasNewsData.formData.st_items.value)
            .pipe(takeUntil(this.destroy));

          this.newsTeasers$.subscribe({
            error: (error) =>
              console.error(
                'TeaserContainer > newsTeasers$ (manual) subscription error:',
                error
              ),
          });
        }
      },
      'setCaasNews.newsTeasers$ (manual)',
      this.errorHandler
    );
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
